/**
 * All-day calendar events as day-header chips.
 *
 * All-day events arrive from `/calendar_events` as ordinary midnight-to-
 * midnight timed events, so without this they paint as a full-height block
 * that swallows the day column. Detection lives in
 * `@/api/calendarEvents#partitionAllDayEvents`; this only renders.
 *
 * Deliberately NOT a kula-ai `Tag`: the chip has to be visually identical to
 * the in-grid event block (per-interviewer pastel fill, dashed border for
 * free, hatch for meeting rooms), so it takes the SAME resolved colours and
 * class names the grid uses and lets the shared event CSS style it.
 *
 * Presentational only — callers resolve colours, so this stays usable by
 * every InterviewManager calendar regardless of its colour source.
 */
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import { Box } from '@chakra-ui/react';
import { ALL_DAY_EVENT_FALLBACK_TITLE } from './allDayChipCopy';
import './allDayEventChip.css';

export interface AllDayChipEvent {
  id?: string;
  title?: string;
  /** Resolved by the caller so the chip matches its grid block exactly. */
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  /** `im-interviewer-busy` | `im-interviewer-free` | `im-meeting-room-busy`. */
  className?: string;
  /**
   * Marked Free in the provider calendar. Drives the day wash
   * (`allDayBlockedOverlay`) — a free day is context, never a conflict — so it
   * is semantic rather than derived from `className`.
   */
  isFree?: boolean;
  /**
   * Richer subject from the API, shown on hover where `title` is a friendlier
   * label (meeting rooms carry the room name in `title`, the real meeting
   * here). Falls back to `title`.
   */
  tooltipTitle?: string;
  /**
   * Whose calendar this came from. Only surfaces where a day's grey has to be
   * decided by counting who is out (ScheduleAll, which has no availability
   * model to ask) — the ScheduleInterview calendars gate on their own
   * pipeline instead and leave this unset.
   */
  ownerId?: string;
}

export function AllDayEventChip({
  title,
  backgroundColor,
  borderColor,
  textColor,
  className,
}: AllDayChipEvent) {
  const label = title?.trim() || ALL_DAY_EVENT_FALLBACK_TITLE;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* Day columns are narrow, so the label truncates — the tooltip is
              the only way to read a long title. */}
          <Box
            as="span"
            className={`im-all-day-chip${className ? ` ${className}` : ''}`}
            style={{ backgroundColor, borderColor, color: textColor }}
          >
            {label}
          </Box>
        </TooltipTrigger>
        <TooltipContent side="top">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export interface AllDayEventChipStackProps {
  events: AllDayChipEvent[];
}

/**
 * Every all-day event gets its own chip. The header grows to fit up to
 * `--im-all-day-chip-max` of them (see events.css) and scrolls past that,
 * so a heavy week can't push the time grid off screen.
 */
export function AllDayEventChipStack({ events }: AllDayEventChipStackProps) {
  if (events.length === 0) return null;
  return (
    <Box className="im-all-day-chip-stack">
      {events.map((evt, i) => (
        <AllDayEventChip
          key={evt.id ?? `${evt.title ?? ''}-${i}`}
          title={evt.title}
          backgroundColor={evt.backgroundColor}
          borderColor={evt.borderColor}
          textColor={evt.textColor}
          className={evt.className}
        />
      ))}
    </Box>
  );
}
